import { isNumber } from "lodash";
import { z } from "zod";

// --- Constants ---

export const ELICIT_STUB_TEXT = "Question sent to user. Waiting for response.";

const ALLOWED_PROPERTY_TYPES = new Set(["string", "number", "integer", "boolean", "array"]);
// TODO: Add "date-time" support once a datetime picker component (with time selection) is available.
const ALLOWED_STRING_FORMATS = new Set(["email", "uri", "date"]);
const MAX_PROPERTIES = 7;

// --- Types ---

export interface JsonSchemaEnumOption {
  const: string;
  title: string;
}

export interface JsonSchemaProperty {
  type?: string;
  title?: string;
  description?: string;
  default?: unknown;
  // String constraints
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  format?: string;
  // Number constraints
  minimum?: number;
  maximum?: number;
  // Enum (single-select via oneOf or plain enum)
  oneOf?: JsonSchemaEnumOption[];
  enum?: string[];
  // Array (multi-select)
  items?: {
    anyOf?: JsonSchemaEnumOption[];
    enum?: string[];
  };
  minItems?: number;
  maxItems?: number;
}

export interface ElicitRequestedSchema {
  type: "object";
  properties: Record<string, JsonSchemaProperty>;
  required?: string[];
}

export interface ElicitInput {
  message: string;
  requestedSchema: ElicitRequestedSchema;
}

export interface ElicitResponse {
  action: "accept" | "decline" | "cancel";
  content?: Record<string, unknown>;
  message?: string;
}

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormField {
  key: string;
  type: "text" | "number" | "date" | "datetime" | "email" | "uri" | "boolean" | "enum" | "multi-select";
  title: string;
  description?: string;
  required: boolean;
  isInteger?: boolean;
  defaultValue?: unknown;
  options?: FormFieldOption[];
  constraints?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    minimum?: number;
    maximum?: number;
    minItems?: number;
    maxItems?: number;
  };
}

// --- Validation ---

export type ValidationResult = { valid: true } | { valid: false; errors: string[] };

export function validateRequestedSchema(schema: unknown): ValidationResult {
  const errors: string[] = [];

  if (!schema || typeof schema !== "object") {
    return { valid: false, errors: ["Schema must be an object"] };
  }

  const s = schema as Record<string, unknown>;

  if (s.type !== "object") {
    errors.push('Schema type must be "object"');
  }

  if (!s.properties || typeof s.properties !== "object" || Array.isArray(s.properties)) {
    return { valid: false, errors: ["Schema must have a properties object"] };
  }

  const properties = s.properties as Record<string, unknown>;
  const keys = Object.keys(properties);

  if (keys.length === 0) {
    errors.push("Schema must have at least one property");
  }

  if (keys.length > MAX_PROPERTIES) {
    errors.push(`Schema must have at most ${MAX_PROPERTIES} properties (got ${keys.length})`);
  }

  for (const [key, rawProp] of Object.entries(properties)) {
    if (!rawProp || typeof rawProp !== "object") {
      errors.push(`Property "${key}" must be an object`);
      continue;
    }

    const prop = rawProp as Record<string, unknown>;
    const type = prop.type as string | undefined;

    if (type === "object") {
      errors.push(`Property "${key}" cannot be a nested object`);
      continue;
    }

    if (type && !ALLOWED_PROPERTY_TYPES.has(type)) {
      errors.push(`Property "${key}" has unsupported type "${type}"`);
      continue;
    }

    if (type === "string" && typeof prop.format === "string" && !ALLOWED_STRING_FORMATS.has(prop.format)) {
      errors.push(`Property "${key}" has unsupported string format "${prop.format}"`);
    }

    if (prop.oneOf) {
      if (!Array.isArray(prop.oneOf)) {
        errors.push(`Property "${key}" oneOf must be an array`);
      } else {
        for (let i = 0; i < prop.oneOf.length; i++) {
          const entry = prop.oneOf[i] as Record<string, unknown> | undefined;
          if (!entry || typeof entry.const !== "string" || typeof entry.title !== "string") {
            errors.push(`Property "${key}" oneOf[${i}] must have "const" (string) and "title" (string)`);
          }
        }
      }
    }

    if (type === "array") {
      if (!prop.items || typeof prop.items !== "object") {
        errors.push(`Property "${key}" (array) must have an items object`);
      } else {
        const items = prop.items as Record<string, unknown>;
        if (items.anyOf) {
          if (!Array.isArray(items.anyOf)) {
            errors.push(`Property "${key}" items.anyOf must be an array`);
          } else {
            for (let i = 0; i < items.anyOf.length; i++) {
              const entry = items.anyOf[i] as Record<string, unknown> | undefined;
              if (!entry || typeof entry.const !== "string" || typeof entry.title !== "string") {
                errors.push(`Property "${key}" items.anyOf[${i}] must have "const" (string) and "title" (string)`);
              }
            }
          }
        }
      }
    }
  }

  if (s.required !== undefined) {
    if (!Array.isArray(s.required)) {
      errors.push('"required" must be an array of strings');
    } else {
      for (const req of s.required) {
        if (typeof req !== "string") {
          errors.push(`"required" entries must be strings, got ${typeof req}`);
        } else if (!keys.includes(req)) {
          errors.push(`"required" references non-existent property "${req}"`);
        }
      }
    }
  }

  return errors.length === 0 ? { valid: true } : { valid: false, errors };
}

// --- Zod Conversion ---

export function requestedSchemaToZod(schema: ElicitRequestedSchema): z.ZodObject<any> {
  const shape: Record<string, z.ZodTypeAny> = {};
  const required = new Set(schema.required);

  for (const [key, prop] of Object.entries(schema.properties)) {
    let s: z.ZodTypeAny;
    const isRequired = required.has(key);

    const requiredMsg = "This field is required.";

    if (prop.oneOf) {
      const values = prop.oneOf.map((o) => o.const) as [string, ...string[]];
      s = z.enum(values, { message: "Please select an option." });
    } else if (prop.type === "array" && prop.items) {
      const values = prop.items.anyOf
        ? (prop.items.anyOf.map((o) => o.const) as [string, ...string[]])
        : ((prop.items.enum ?? []) as [string, ...string[]]);
      s = z.array(z.enum(values));
      if (prop.minItems !== undefined) {
        s = (s as z.ZodArray<any>).min(prop.minItems, {
          message: prop.minItems === 1 ? "Please select at least one." : `Please select at least ${prop.minItems}.`,
        });
      }
      if (prop.maxItems !== undefined) {
        s = (s as z.ZodArray<any>).max(prop.maxItems, {
          message: `Please select at most ${prop.maxItems}.`,
        });
      }
    } else if (prop.enum) {
      s = z.enum(prop.enum as [string, ...string[]], { message: "Please select an option." });
    } else {
      switch (prop.type) {
        case "string":
          if (prop.format === "date") {
            // Accept both Date objects (from date picker) and date strings (from backend/JSON).
            // Always output an ISO date string for consistent serialization.
            s = z
              .union([z.date(), z.string().date()], { message: isRequired ? requiredMsg : "Please select a date." })
              .transform((val) => (val instanceof Date ? val.toISOString().split("T")[0] : val));
          } else {
            s = z.string({ required_error: requiredMsg });
            if (prop.format === "email") {
              s = (s as z.ZodString).email({ message: "Please enter a valid email address." });
            }
            if (prop.format === "uri") {
              s = (s as z.ZodString).url({ message: "Please enter a valid URL." });
            }
            if (isRequired && !prop.minLength) {
              s = (s as z.ZodString).min(1, { message: requiredMsg });
            }
            if (prop.minLength) {
              s = (s as z.ZodString).min(prop.minLength, {
                message: `Must be at least ${prop.minLength} characters.`,
              });
            }
            if (prop.maxLength) {
              s = (s as z.ZodString).max(prop.maxLength, {
                message: `Must be at most ${prop.maxLength} characters.`,
              });
            }
            if (prop.pattern) {
              try {
                s = (s as z.ZodString).regex(new RegExp(prop.pattern), {
                  message: "Invalid format.",
                });
              } catch {
                // Invalid regex from LLM — skip rather than crash
              }
            }
          }
          break;
        case "number":
        case "integer":
          s = z.number({ required_error: requiredMsg, message: "Please enter a number." });
          if (prop.type === "integer") {
            s = (s as z.ZodNumber).int({ message: "Must be a whole number." });
          }
          if (isNumber(prop.minimum)) {
            s = (s as z.ZodNumber).min(prop.minimum, { message: `Must be at least ${prop.minimum}.` });
          }
          if (isNumber(prop.maximum)) {
            s = (s as z.ZodNumber).max(prop.maximum, { message: `Must be at most ${prop.maximum}.` });
          }
          break;
        case "boolean":
          s = z.boolean();
          break;
        default:
          s = z.unknown();
      }
    }

    shape[key] = isRequired ? s : s.optional();
  }
  return z.object(shape);
}

// --- Default Values ---

export function requestedSchemaToDefaultValues(schema: ElicitRequestedSchema): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  for (const [key, prop] of Object.entries(schema.properties)) {
    if (prop.default !== undefined) {
      defaults[key] = prop.default;
    } else if (prop.type === "array") {
      defaults[key] = [];
    }
  }
  return defaults;
}

// --- Form Fields ---

function extractOptions(entries: JsonSchemaEnumOption[]): FormFieldOption[] {
  return entries.map((e) => ({ value: e.const, label: e.title }));
}

function plainEnumToOptions(values: string[]): FormFieldOption[] {
  return values.map((v) => ({ value: v, label: v }));
}

export function requestedSchemaToFormFields(schema: ElicitRequestedSchema): FormField[] {
  const required = new Set(schema.required);

  return Object.entries(schema.properties).map(([key, prop]) => {
    const base = {
      key,
      title: prop.title ?? key,
      description: prop.description,
      required: required.has(key),
      defaultValue: prop.default,
    };

    // Single-select enum via oneOf
    if (prop.oneOf) {
      return {
        ...base,
        type: "enum" as const,
        options: extractOptions(prop.oneOf),
      };
    }

    // Multi-select via array
    if (prop.type === "array" && prop.items) {
      let options: FormFieldOption[] = [];
      if (prop.items.anyOf) {
        options = extractOptions(prop.items.anyOf);
      } else if (prop.items.enum) {
        options = plainEnumToOptions(prop.items.enum);
      }
      return {
        ...base,
        type: "multi-select" as const,
        options,
        constraints: {
          ...(prop.minItems !== undefined && { minItems: prop.minItems }),
          ...(prop.maxItems !== undefined && { maxItems: prop.maxItems }),
        },
      };
    }

    // Plain enum (no labels)
    if (prop.enum) {
      return {
        ...base,
        type: "enum" as const,
        options: plainEnumToOptions(prop.enum),
      };
    }

    switch (prop.type) {
      case "string": {
        let type: FormField["type"];
        switch (prop.format) {
          case "date":
            type = "date";
            break;
          case "email":
            type = "email";
            break;
          case "uri":
            type = "uri";
            break;
          default:
            type = "text";
            break;
        }

        return {
          ...base,
          type,
          constraints: {
            ...(prop.minLength !== undefined && { minLength: prop.minLength }),
            ...(prop.maxLength !== undefined && { maxLength: prop.maxLength }),
            ...(prop.pattern !== undefined && { pattern: prop.pattern }),
          },
        };
      }
      case "number":
      case "integer":
        return {
          ...base,
          type: "number" as const,
          isInteger: prop.type === "integer",
          constraints: {
            ...(prop.minimum !== undefined && { minimum: prop.minimum }),
            ...(prop.maximum !== undefined && { maximum: prop.maximum }),
          },
        };
      case "boolean":
        return { ...base, type: "boolean" as const };
      default:
        return { ...base, type: "text" as const };
    }
  });
}
